import React, { useEffect, useRef } from 'react';
import { renderMarkdown, renderInlineMarkdown } from '../lib/markdown';
import { loadKatex } from '../lib/katexLoader';

/**
 * Renders authored content (question text, answer options, explanations) that
 * may embed images.
 *
 * The HTML is produced by lib/markdown, which escapes the source before any tag
 * is emitted and only allows same-origin /uploads images — so nothing an author
 * types can inject markup. That is what makes dangerouslySetInnerHTML safe here.
 *
 * `inline` skips block wrappers (tables, paragraphs, lists) — use for options
 * and other short content inside buttons. Question stems must use the default
 * block mode so markdown tables/charts render for students.
 *
 * Math (`$..$` / `$$..$$`) renders server-side as an escaped-TeX fallback span
 * (`.katex-target[data-tex]`) so it's never blank content. KaTeX itself is only
 * imported — lazily, client-side — when such a span is actually present, so
 * questions without formulas never pay for the library.
 */
export function RichText({
  value,
  inline = false,
  className,
}: {
  value: string;
  inline?: boolean;
  className?: string;
}) {
  const ref = useRef<HTMLElement>(null);
  const html = inline ? renderInlineMarkdown(value) : renderMarkdown(value);
  const Tag = inline ? 'span' : 'div';
  const classes = [
    'rich-text',
    inline ? 'rich-text--inline' : 'rich-text--block',
    className,
  ].filter(Boolean).join(' ');

  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    const targets = root.querySelectorAll<HTMLElement>('.katex-target[data-tex]');
    if (targets.length === 0) return;
    let cancelled = false;
    loadKatex().then((katex) => {
      if (cancelled) return;
      targets.forEach((el) => {
        const tex = el.dataset.tex ?? '';
        try {
          katex.render(tex, el, { throwOnError: false, displayMode: el.classList.contains('katex-block') });
        } catch {
          // Leave the escaped-TeX fallback text in place.
        }
      });
    }).catch(() => { /* offline or blocked — fallback text stays visible */ });
    return () => { cancelled = true; };
  }, [html]);

  return (
    <Tag
      ref={ref as React.RefObject<HTMLDivElement & HTMLSpanElement>}
      className={classes}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
